import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { 
  Users, 
  Plus, 
  Shield, 
  User, 
  Mail,
  Calendar,
  Edit,
  Trash2,
  Key,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDefaultAdminWarning, setShowDefaultAdminWarning] = useState(false);

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'technicien'
  });

  const [editUser, setEditUser] = useState({
    username: '',
    email: '',
    role: 'technicien'
  });

  const [passwordData, setPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      
      // Check if default admin exists and there are other admins
      const defaultAdmin = response.data.find(u => u.username === 'admin');
      const adminCount = response.data.filter(u => u.role === 'admin').length;
      
      if (defaultAdmin && adminCount > 1) {
        setShowDefaultAdminWarning(true);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.username || !newUser.email || !newUser.password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Basic email validation
    if (!/\S+@\S+\.\S+/.test(newUser.email)) {
      toast.error('Format d\'email invalide');
      return;
    }

    if (newUser.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setIsAddingUser(true);

    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success('Utilisateur créé avec succès');
      setShowAddDialog(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'technicien'
      });
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'utilisateur');
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!editUser.username || !editUser.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.put(`${API}/users/${selectedUser.id}`, editUser);
      toast.success('Utilisateur modifié avec succès');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.new_password) {
      toast.error('Veuillez entrer un nouveau mot de passe');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      await axios.put(`${API}/users/${selectedUser.id}/password`, {
        new_password: passwordData.new_password
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('Utilisateur supprimé avec succès');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteDefaultAdmin = async () => {
    const defaultAdmin = users.find(u => u.username === 'admin');
    if (defaultAdmin) {
      try {
        await axios.delete(`${API}/users/${defaultAdmin.id}`);
        toast.success('Compte administrateur par défaut supprimé');
        setShowDefaultAdminWarning(false);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting default admin:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      }
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditUser({
      username: user.username,
      email: user.email,
      role: user.role
    });
    setShowEditDialog(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: '', confirm_password: '' });
    setShowPasswordDialog(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const getRoleBadge = (role) => {
    const roleMap = {
      'admin': { label: 'Administrateur', className: 'bg-red-100 text-red-800 border-red-200' },
      'technicien': { label: 'Technicien', className: 'bg-blue-100 text-blue-800 border-blue-200' }
    };
    
    const roleInfo = roleMap[role] || { label: role, className: 'bg-gray-100 text-gray-800 border-gray-200' };
    
    return (
      <Badge className={`${roleInfo.className} border`}>
        <div className="flex items-center space-x-1">
          {role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
          <span>{roleInfo.label}</span>
        </div>
      </Badge>
    );
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Card className="glass">
        <CardContent className="text-center py-12">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Accès restreint</h3>
          <p className="text-slate-600">
            Seuls les administrateurs peuvent accéder à la gestion des utilisateurs.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="user-management">
      {/* Default Admin Warning */}
      {showDefaultAdminWarning && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900">Compte administrateur par défaut détecté</h3>
                <p className="text-sm text-orange-800 mt-1">
                  Pour des raisons de sécurité, il est recommandé de supprimer le compte admin par défaut maintenant que vous avez créé un nouveau compte administrateur.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleDeleteDefaultAdmin}
                  >
                    Supprimer le compte par défaut
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDefaultAdminWarning(false)}
                  >
                    Plus tard
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des utilisateurs</h1>
          <p className="text-slate-600 mt-1">Gérer les comptes utilisateurs du système</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button 
              className="mt-4 sm:mt-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 btn-hover-lift"
              data-testid="add-user-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
              <DialogDescription>
                Ajouter un nouveau compte utilisateur au système
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_username">Nom d'utilisateur *</Label>
                <Input
                  id="new_username"
                  value={newUser.username}
                  onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Nom d'utilisateur unique"
                  className="input-field"
                  data-testid="new-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_email">Email *</Label>
                <Input
                  id="new_email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@smartservicemobile.be"
                  className="input-field"
                  data-testid="new-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">Mot de passe *</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Minimum 6 caractères"
                  className="input-field"
                  data-testid="new-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_role">Rôle</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="new-role-select">
                    <SelectValue placeholder="Sélectionnez un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technicien">Technicien</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={isAddingUser}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  data-testid="create-user-button"
                >
                  {isAddingUser ? (
                    <div className="flex items-center">
                      <div className="spinner w-4 h-4 mr-2"></div>
                      Création...
                    </div>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Créer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <div className="space-y-4">
        {users.length > 0 ? (
          users.map((user, index) => (
            <Card key={user.id} className="glass card-hover slide-up" style={{ animationDelay: `${index * 50}ms` }}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* User Info */}
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-slate-400 to-slate-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg font-medium">
                        {user.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900" data-testid={`user-username-${index}`}>
                          {user.username}
                        </h3>
                        {getRoleBadge(user.role)}
                        {!user.is_active && (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                            Inactif
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center space-x-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          <span>{user.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-slate-500">
                          <Calendar className="w-4 h-4" />
                          <span>Créé le {new Date(user.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center space-x-2 lg:space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                      <span className="text-sm text-slate-600">
                        {user.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(user)}
                        className="btn-hover-lift"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPasswordDialog(user)}
                        className="btn-hover-lift"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => openDeleteDialog(user)}
                        disabled={user.id === currentUser.id}
                        className="btn-hover-lift"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="glass">
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun utilisateur trouvé</h3>
              <p className="text-slate-600 mb-6">
                Commencez par créer le premier utilisateur du système.
              </p>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un utilisateur
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Summary */}
      {users.length > 0 && (
        <Card className="glass">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm text-slate-600">
              <div className="flex items-center space-x-4">
                <span>{users.length} utilisateur(s) au total</span>
                <span>•</span>
                <span>{users.filter(u => u.role === 'admin').length} administrateur(s)</span>
                <span>•</span>
                <span>{users.filter(u => u.role === 'technicien').length} technicien(s)</span>
              </div>
              <div className="mt-2 sm:mt-0">
                <span>{users.filter(u => u.is_active).length} actif(s)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>
              Modifier les informations de l'utilisateur
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_username">Nom d'utilisateur *</Label>
              <Input
                id="edit_username"
                value={editUser.username}
                onChange={(e) => setEditUser(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Nom d'utilisateur"
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email *</Label>
              <Input
                id="edit_email"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_role">Rôle</Label>
              <Select 
                value={editUser.role} 
                onValueChange={(value) => setEditUser(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technicien">Technicien</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Changer le mot de passe</DialogTitle>
            <DialogDescription>
              Définir un nouveau mot de passe pour {selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe *</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Minimum 6 caractères"
                className="input-field"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le mot de passe *</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Retaper le mot de passe"
                className="input-field"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordDialog(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Modifier
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Alert Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser?.username}</strong> ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;